import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import IndustrySelect from "@/components/IndustrySelect";
import { ClientContactPicker } from "@/components/ClientContactPicker";
import FileDrop from "@/components/FileDrop";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sparkles, Loader2, FileText, Pencil, Plus, Check, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);
const emptyForm = () => ({ title: "", client_id: "", client_ref: "", end_user: "", end_client_id: "", buyer: "", client_contact_name: "", client_contact_email: "", rfq_issued_date: "", date_received: today(), quote_deadline: "", delivery_location: "", acknowledged: false });
const NONE = "__none__";

function readFileBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function CreateRFQDialog({ open, onOpenChange, clients, onClientCreated, initialClientId, initialTitle, onCreated, prefill }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  useEffect(() => { if (open) api.get("/team-members").then((r) => setMembers(r.data)).catch(() => {}); }, [open]);
  const [mode, setMode] = useState("choose"); // choose | doc | form
  const [source, setSource] = useState("text");
  const [emailText, setEmailText] = useState("");
  const [file, setFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [items, setItems] = useState([]);
  const [sourceKind, setSourceKind] = useState("manual");
  const [addingClient, setAddingClient] = useState(false);
  const [addingFor, setAddingFor] = useState("client");
  const [extraFiles, setExtraFiles] = useState([]);
  const [newClient, setNewClient] = useState({ name: "", industry: "mining" });
  const [suggestedClient, setSuggestedClient] = useState("");

  const reset = () => {
    setMode("choose"); setSource("text"); setEmailText(""); setFile(null); setExtraFiles([]); setAddingFor("client");
    setForm(emptyForm()); setItems([]); setSourceKind("manual"); setAddingClient(false);
    setNewClient({ name: "", industry: "mining" }); setSuggestedClient("");
  };
  const close = (v) => { if (!v) reset(); onOpenChange(v); };

  // When launched from an Opportunity, jump straight to the form pre-filled with the client.
  useEffect(() => {
    if (open && initialClientId) {
      setMode("form");
      setForm({ ...emptyForm(), client_id: initialClientId, client_ref: initialTitle || "" });
      setItems([]);
      setSourceKind("manual");
    }
  }, [open, initialClientId, initialTitle]);

  // Launched from the Outlook add-in: load the email/attachment and read it straight away.
  useEffect(() => {
    if (!open || !prefill) return;
    setMode("doc"); setSource(prefill.source); setEmailText(prefill.text || ""); setFile(prefill.file || null); setExtraFiles(prefill.extras || []);
    analyze(prefill);
  }, [open, prefill]); // eslint-disable-line react-hooks/exhaustive-deps

  const analyze = async (over) => {
    const src = over?.source || source, txt = over ? over.text || "" : emailText, f = over ? over.file : file;
    setAnalyzing(true);
    try {
      let payload;
      if (src === "text") {
        if (!txt.trim()) { toast.error("Paste the client's RFQ email first."); return; }
        payload = { source_type: "text", text: txt };
      } else {
        if (!f) { toast.error("Choose a file first."); return; }
        payload = { source_type: src, file_base64: await readFileBase64(f), filename: f.name, text: txt };
      }
      const { data } = await api.post("/rfqs/analyze", payload);
      setForm({
        title: data.title || "",
        client_id: data.matched_client_id || "",
        client_ref: data.client_ref || data.title || "",
        end_user: data.end_client_name || "",
        end_client_id: data.matched_end_client_id || "",
        buyer: data.buyer || "",
        rfq_issued_date: data.rfq_issued_date || "",
        date_received: data.date_received || today(),
        quote_deadline: data.quote_deadline || "",
        delivery_location: data.delivery_location || "",
        acknowledged: false,
      });
      setItems(data.items || []);
      setSourceKind(src);
      setSuggestedClient(data.matched_client_id ? "" : (data.client_name || ""));
      setMode("form");
      const parts = [`${(data.items || []).length} line item(s)`];
      if (data.matched_client_id) parts.push("matched a client");
      else if (data.client_name) parts.push(`client "${data.client_name}" — please confirm`);
      toast.success(`AI read your RFQ: ${parts.join(", ")}`);
    } catch (e) { toast.error(apiError(e)); } finally { setAnalyzing(false); }
  };

  const startManual = () => { setForm(emptyForm()); setItems([]); setSourceKind("manual"); setMode("form"); };

  const addClient = async () => {
    if (!newClient.name.trim()) { toast.error("Enter the client name."); return; }
    try {
      const { data } = await api.post("/clients", { name: newClient.name.trim(), industry: newClient.industry });
      onClientCreated && onClientCreated(data);
      setForm((f) => (addingFor === "end" ? { ...f, end_client_id: data.id } : { ...f, client_id: data.id }));
      setAddingClient(false); setNewClient({ name: "", industry: "mining" });
      toast.success("Client added");
    } catch (e) { toast.error(apiError(e)); }
  };

  const create = async () => {
    if (!form.client_ref.trim()) { toast.error("Enter the client's RFQ reference."); return; }
    if (!form.client_id) { toast.error("Select or add a client."); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/rfqs", { ...form, title: form.client_ref.trim(), source: sourceKind, stage: "new", owner_id: form.owner_id || user?.id });
      if (items.length) {
        await api.post(`/rfqs/${data.id}/items/bulk`, {
          items: items.map((i) => ({ description: i.description, quantity: i.quantity, unit: i.unit, spec_or_part: i.spec_or_part })),
        });
      }
      // Keep the original RFQ document for reference on the detail page (non-blocking).
      try {
        if (file && ["pdf", "image", "excel"].includes(sourceKind)) {
          const b64 = await readFileBase64(file);
          await api.post(`/rfqs/${data.id}/source-files`, {
            filename: file.name, content_type: file.type || "application/octet-stream", file_base64: b64,
          });
        } else if (sourceKind === "text" && emailText.trim()) {
          const b64 = btoa(unescape(encodeURIComponent(emailText)));
          await api.post(`/rfqs/${data.id}/source-files`, {
            filename: `${(form.client_ref || "RFQ").slice(0, 40)} — client email.txt`,
            content_type: "text/plain", file_base64: b64,
          });
        }
      } catch (_) { /* source-file save is best-effort */ }
      for (const f of extraFiles) {
        try {
          await api.post(`/rfqs/${data.id}/source-files`, { filename: f.name, content_type: f.type || "application/octet-stream", file_base64: await readFileBase64(f) });
        } catch (_) { toast.error(`Could Not Attach ${f.name}`); }
      }
      onCreated && onCreated(data);
      toast.success(`RFQ ${data.code || ""} created`);
      close(false);
      if (!prefill) navigate(`/rfqs/${data.id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setCreating(false); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="rfq-dialog">
        <DialogHeader>
          <DialogTitle>
            {mode === "choose" && "New RFQ"}
            {mode === "doc" && "Read RFQ from a document"}
            {mode === "form" && "Confirm RFQ Details"}
          </DialogTitle>
        </DialogHeader>

        {mode === "choose" && (
          <div className="grid gap-3 py-2 sm:grid-cols-2">
            <button onClick={() => setMode("doc")} className="group rounded-lg border border-border p-5 text-left transition-colors hover:border-primary hover:bg-slate-50" data-testid="mode-document">
              <div className="mb-2 inline-flex rounded-md bg-[#E6ECF5] p-2 text-primary"><Sparkles className="h-5 w-5" /></div>
              <div className="font-heading font-bold text-slate-900">From A Document</div>
              <p className="mt-1 text-sm text-muted-foreground">Drop the client's email, PDF, image or Excel. AI fills in the details and line items for you.</p>
            </button>
            <button onClick={startManual} className="group rounded-lg border border-border p-5 text-left transition-colors hover:border-primary hover:bg-slate-50" data-testid="mode-manual">
              <div className="mb-2 inline-flex rounded-md bg-[#F6EDD9] p-2 text-[#A87F31]"><Pencil className="h-5 w-5" /></div>
              <div className="font-heading font-bold text-slate-900">Enter Manually</div>
              <p className="mt-1 text-sm text-muted-foreground">Came in by phone or ad-hoc? Type the details yourself and add items later.</p>
            </button>
          </div>
        )}

        {mode === "doc" && (
          <div className="space-y-4">
            <Tabs value={source} onValueChange={(v) => { setSource(v); setFile(null); }}>
              <TabsList className="flex h-auto flex-wrap justify-start gap-1" data-testid="rfq-source-tabs">
                <TabsTrigger value="text" data-testid="src-text">Paste Email</TabsTrigger>
                <TabsTrigger value="pdf" data-testid="src-pdf">PDF</TabsTrigger>
                <TabsTrigger value="image" data-testid="src-image">Image</TabsTrigger>
                <TabsTrigger value="excel" data-testid="src-excel">Excel</TabsTrigger>
              </TabsList>
              <TabsContent value="text" className="mt-3">
                <Textarea rows={7} placeholder="Paste the client's RFQ email body here..." value={emailText} onChange={(e) => setEmailText(e.target.value)} data-testid="rfq-email-textarea" />
              </TabsContent>
              <TabsContent value="pdf" className="mt-3"><FileDrop accept="application/pdf" files={file ? [file] : []} onFiles={(f) => setFile(f[0] || null)} hint="PDF" testid="rfq-pdf-drop" /></TabsContent>
              <TabsContent value="image" className="mt-3"><FileDrop accept="image/*" files={file ? [file] : []} onFiles={(f) => setFile(f[0] || null)} hint="JPG, PNG" testid="rfq-image-drop" /></TabsContent>
              <TabsContent value="excel" className="mt-3"><FileDrop accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" files={file ? [file] : []} onFiles={(f) => setFile(f[0] || null)} hint="XLSX, XLS" testid="rfq-excel-drop" /></TabsContent>
            </Tabs>
            <DialogFooter className="gap-2">
              <Button variant="ghost" onClick={() => setMode("choose")}><ArrowLeft className="mr-2 h-4 w-4" />Back</Button>
              <Button onClick={() => analyze()} disabled={analyzing} data-testid="rfq-analyze-button">
                {analyzing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
                {analyzing ? "Reading..." : "Analyze with AI"}
              </Button>
            </DialogFooter>
          </div>
        )}

        {mode === "form" && (
          <div className="space-y-4">
            {items.length > 0 && (
              <div className="flex items-center gap-2 rounded-md border border-[#C4DACE] bg-[#E4EEE8] px-3 py-2 text-sm text-[#2F6B45]" data-testid="rfq-items-preview">
                <FileText className="h-4 w-4" /> {items.length} line item(s) extracted — they'll be added automatically.
              </div>
            )}
            <div className="space-y-2">
              <Label>Client's RFQ Reference</Label>
              <Input value={form.client_ref} onChange={(e) => setForm({ ...form, client_ref: e.target.value })} placeholder="Their enquiry / ref no." data-testid="rfq-clientref-input" />
            </div>
            <div className="space-y-2">
              <Label>Client</Label>
              {!addingClient || addingFor !== "client" ? (
                <div className="flex gap-2">
                  <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                    <SelectTrigger className="flex-1" data-testid="rfq-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                    <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button type="button" variant="outline" onClick={() => { setAddingFor("client"); setAddingClient(true); }} data-testid="rfq-add-client-toggle"><Plus className="mr-1 h-4 w-4" />New</Button>
                </div>
              ) : (                <div className="flex flex-wrap gap-2 rounded-md border border-dashed border-slate-300 p-2">
                  <Input className="min-w-[10rem] flex-1" placeholder="New client name" value={newClient.name} onChange={(e) => setNewClient({ ...newClient, name: e.target.value })} data-testid="rfq-new-client-name" />
                  <IndustrySelect className="w-40" value={newClient.industry} onChange={(v) => setNewClient({ ...newClient, industry: v })} testid="rfq-new-client-industry" />
                  <Button type="button" onClick={addClient} data-testid="rfq-new-client-save"><Check className="mr-1 h-4 w-4" />Add</Button>
                  <Button type="button" variant="ghost" onClick={() => setAddingClient(false)}>Cancel</Button>
                </div>
              )}
              {suggestedClient && !form.client_id && !addingClient && (
                <p className="text-xs text-muted-foreground" data-testid="rfq-suggested-client">
                  AI read the client as <span className="font-medium text-slate-700">"{suggestedClient}"</span> but found no match.{" "}
                  <button type="button" className="font-medium text-primary hover:underline" onClick={() => { setNewClient({ name: suggestedClient, industry: "mining" }); setAddingFor("client"); setAddingClient(true); }}>
                    Add as new client
                  </button>
                </p>
              )}
            </div>
            {form.client_id && <ClientContactPicker key={form.client_id} clientId={form.client_id} form={form} setForm={setForm} />}
            <div className="space-y-2">
              <Label>End Client <span className="font-normal text-muted-foreground">(Optional — If The Client Is A Subcontractor)</span></Label>
              {!addingClient || addingFor !== "end" ? (
                <div className="flex gap-2">
                  <Select value={form.end_client_id || NONE} onValueChange={(v) => setForm({ ...form, end_client_id: v === NONE ? "" : v })}>
                    <SelectTrigger className="min-w-0 flex-1 [&>span]:truncate" data-testid="rfq-endclient-select"><SelectValue placeholder="Select end client" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>None — Client Is The End Client</SelectItem>
                      {clients.filter((c) => c.id !== form.client_id).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button type="button" variant="outline" className="shrink-0" onClick={() => { setAddingFor("end"); setNewClient({ name: form.end_client_id ? "" : form.end_user || "", industry: "mining" }); setAddingClient(true); }} data-testid="rfq-add-endclient-toggle"><Plus className="mr-1 h-4 w-4" />New</Button>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2 rounded-md border border-dashed border-slate-300 p-2" data-testid="rfq-new-endclient-form">
                  <Input className="min-w-[10rem] flex-1" placeholder="New end client name" value={newClient.name} onChange={(e) => setNewClient({ ...newClient, name: e.target.value })} data-testid="rfq-new-endclient-name" />
                  <IndustrySelect className="w-40" value={newClient.industry} onChange={(v) => setNewClient({ ...newClient, industry: v })} testid="rfq-new-endclient-industry" />
                  <Button type="button" onClick={addClient} data-testid="rfq-new-endclient-save"><Check className="mr-1 h-4 w-4" />Add</Button>
                  <Button type="button" variant="ghost" onClick={() => setAddingClient(false)}>Cancel</Button>
                </div>
              )}
              {!form.end_client_id && form.end_user && !addingClient && <p className="text-xs text-muted-foreground" data-testid="rfq-suggested-endclient">AI read the end client as <span className="font-medium text-slate-700">"{form.end_user}"</span> — select it or tap New.</p>}
            </div>
            <div className="space-y-2">
              <Label>RFQ Acknowledged?</Label>
              <div className="flex gap-2">
                <button type="button" data-testid="rfq-ack-yes" onClick={() => setForm({ ...form, acknowledged: true })}
                  className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium ${form.acknowledged ? "border-[#2F6B45] bg-[#E4EEE8] text-[#2F6B45]" : "border-slate-200 text-slate-600"}`}>
                  Yes I Acknowledged
                </button>
                <button type="button" data-testid="rfq-ack-no" onClick={() => setForm({ ...form, acknowledged: false })}
                  className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium ${!form.acknowledged ? "border-primary bg-primary/10 text-primary" : "border-slate-200 text-slate-600"}`}>
                  Not Yet
                </button>
              </div>
              {!form.acknowledged && (
                <p className="rounded-md border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-900" data-testid="rfq-ack-guide">
                  After Saving, The Acknowledgement Draft (With The Client's Tracking Link) Opens Ready To Copy Into Your Reply.
                </p>
              )}
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2 min-w-0">
                <Label>RFQ Issued Date</Label>
                <Input type="date" value={form.rfq_issued_date} onChange={(e) => setForm({ ...form, rfq_issued_date: e.target.value })} data-testid="rfq-issued-date-input" className="w-full min-w-0" />
                <p className="text-[11px] text-muted-foreground">The Date Printed On The Client's RFQ</p>
              </div>
              <div className="space-y-2 min-w-0">
                <Label>Date Received</Label>
                <Input type="date" value={form.date_received} onChange={(e) => setForm({ ...form, date_received: e.target.value })} data-testid="rfq-date-input" className="w-full min-w-0" />
                <p className="text-[11px] text-muted-foreground">When The Client Actually Sent It To Us</p>
              </div>
              <div className="space-y-2 min-w-0">
                <Label>Quote Deadline</Label>
                <Input type="date" value={form.quote_deadline} onChange={(e) => setForm({ ...form, quote_deadline: e.target.value })} data-testid="rfq-deadline-input" className="w-full min-w-0" />
              </div>
              <div className="space-y-2">
                <Label>Buyer <span className="font-normal text-muted-foreground">(Optional)</span></Label>
                <Input value={form.buyer} onChange={(e) => setForm({ ...form, buyer: e.target.value })} placeholder="Requestor / End User At The Site" data-testid="rfq-buyer-input" />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>RFQ Owner</Label>
                <Select value={form.owner_id || user?.id || ""} onValueChange={(v) => setForm({ ...form, owner_id: v })}>
                  <SelectTrigger data-testid="rfq-owner-select"><SelectValue placeholder="Select owner" /></SelectTrigger>
                  <SelectContent>
                    {members.map((m) => <SelectItem key={m.id} value={m.id}>{m.full_name || m.name}{m.job_title ? ` — ${m.job_title}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>{sourceKind === "manual" ? "Attach RFQ Documents" : "Attach More Documents"} <span className="font-normal text-muted-foreground">(Optional)</span></Label>
              <FileDrop multiple files={extraFiles} onFiles={setExtraFiles} hint="Drawings, Specs, The Client's Email — Any File Type" testid="rfq-extra-drop" />
            </div>
            {form.quote_deadline && form.date_received && form.quote_deadline < form.date_received && (
              <p className="text-sm text-amber-600" data-testid="rfq-deadline-warning">Quote deadline is before the date received — double-check the dates.</p>
            )}
            <DialogFooter className="gap-2">
              <Button variant="ghost" onClick={() => setMode("choose")}><ArrowLeft className="mr-2 h-4 w-4" />Back</Button>
              <Button onClick={create} disabled={creating || !form.client_ref || !form.client_id} data-testid="rfq-create-submit">
                {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Create &amp; Continue
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
